import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Redirect } from "react-router-dom";
import { useUnsavedChanges } from "hooks/useUnsavedChanges";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import VuiBadge from "components/VuiBadge";
import { useAuthz } from "context/AuthzContext";
import { useCompany } from "context/CompanyContext";
import {
  backupTenant,
  createOrg,
  createTenantUserInvite,
  deleteOrg,
  deleteTenantUser,
  listTenantUsers,
  restoreTenant,
  updateOrg,
} from "services/tenantUserAdminService";
import { TC } from "../tokens/text";

const INITIAL_FORM = { name: "", address: "", legalForm: "", utr: "", vatNumber: "" };

function OrgCreateDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const initialSnap = useRef(null);
  const isDirty = open && initialSnap.current !== null && JSON.stringify(form) !== initialSnap.current;
  const { guardClose, ConfirmDialog } = useUnsavedChanges(isDirty);

  useEffect(() => {
    if (open) { setForm(INITIAL_FORM); initialSnap.current = JSON.stringify(INITIAL_FORM); setError(""); }
    else       { initialSnap.current = null; }
  }, [open]);

  const handleChange = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Organisation name is required."); return; }
    setIsSubmitting(true);
    setError("");
    try {
      const org = await createOrg(form);
      setForm(INITIAL_FORM);
      onCreated(org);
    } catch (err) {
      setError(err?.message || "Failed to create organisation.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => { if (!isSubmitting) guardClose(() => { setForm(INITIAL_FORM); setError(""); onClose(); }); };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#ffffff",
          color: TC.primary,
          border: "1px solid #d8e6f7",
          borderRadius: "16px",
        },
      }}
    >
      <DialogTitle sx={{ color: TC.primary }}>Create Organisation</DialogTitle>
      <DialogContent>
        {error ? (
          <VuiTypography color="error" variant="button" mb={2} display="block">{error}</VuiTypography>
        ) : null}
        <VuiBox component="form" id="org-form" onSubmit={handleSubmit}>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <VuiInput placeholder="Organisation Name *" value={form.name} onChange={handleChange("name")} required />
            </Grid>
            <Grid item xs={12}>
              <VuiInput placeholder="Address" value={form.address} onChange={handleChange("address")} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VuiInput placeholder="Legal Form (e.g. Ltd, Sole Trader)" value={form.legalForm} onChange={handleChange("legalForm")} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VuiInput placeholder="UTR Number" value={form.utr} onChange={handleChange("utr")} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VuiInput placeholder="VAT Number" value={form.vatNumber} onChange={handleChange("vatNumber")} />
            </Grid>
          </Grid>
        </VuiBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <VuiButton color="text" size="small" onClick={handleClose} disabled={isSubmitting}>Cancel</VuiButton>
        <VuiButton color="info" size="small" type="submit" form="org-form" disabled={isSubmitting}>
          {isSubmitting ? "Creating..." : "Create Organisation"}
        </VuiButton>
      </DialogActions>
    </Dialog>
    {ConfirmDialog}
    </>
  );
}

function OrgEditDialog({ open, org, onClose, onUpdated }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const initialSnap = useRef(null);
  const isDirty = open && initialSnap.current !== null && JSON.stringify(form) !== initialSnap.current;
  const { guardClose, ConfirmDialog } = useUnsavedChanges(isDirty);

  useEffect(() => {
    if (org) {
      const fresh = {
        name: org.name || "",
        address: org.address || "",
        legalForm: org.legalForm || "",
        utr: org.utr || "",
        vatNumber: org.vatNumber || "",
      };
      setForm(fresh);
      initialSnap.current = JSON.stringify(fresh);
      setError("");
    } else {
      initialSnap.current = null;
    }
  }, [org]);

  const handleChange = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Organisation name is required."); return; }
    setIsSubmitting(true);
    setError("");
    try {
      const updated = await updateOrg(org.id, form);
      onUpdated(updated);
    } catch (err) {
      setError(err?.message || "Failed to update organisation.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => { if (!isSubmitting) guardClose(() => { setError(""); onClose(); }); };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#ffffff",
          color: TC.primary,
          border: "1px solid #d8e6f7",
          borderRadius: "16px",
        },
      }}
    >
      <DialogTitle sx={{ color: TC.primary }}>Edit Organisation</DialogTitle>
      <DialogContent>
        {error ? (
          <VuiTypography color="error" variant="button" mb={2} display="block">{error}</VuiTypography>
        ) : null}
        <VuiBox component="form" id="org-edit-form" onSubmit={handleSubmit}>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <VuiInput placeholder="Organisation Name *" value={form.name} onChange={handleChange("name")} required />
            </Grid>
            <Grid item xs={12}>
              <VuiInput placeholder="Address" value={form.address} onChange={handleChange("address")} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VuiInput placeholder="Legal Form (e.g. Ltd, Sole Trader)" value={form.legalForm} onChange={handleChange("legalForm")} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VuiInput placeholder="UTR Number" value={form.utr} onChange={handleChange("utr")} />
            </Grid>
            <Grid item xs={12} md={6}>
              <VuiInput placeholder="VAT Number" value={form.vatNumber} onChange={handleChange("vatNumber")} />
            </Grid>
          </Grid>
        </VuiBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <VuiButton color="text" size="small" onClick={handleClose} disabled={isSubmitting}>Cancel</VuiButton>
        <VuiButton color="info" size="small" type="submit" form="org-edit-form" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : "Save Changes"}
        </VuiButton>
      </DialogActions>
    </Dialog>
    {ConfirmDialog}
    </>
  );
}

function AssignTenantAdminDialog({ open, org, onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", displayName: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setForm({ email: "", displayName: "" }); setError(""); }
  }, [open]);

  const handleChange = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { setError("Email is required."); return; }
    if (!form.displayName.trim()) { setError("Display name is required."); return; }
    setIsSubmitting(true);
    setError("");
    try {
      const result = await createTenantUserInvite({
        email: form.email.trim(),
        displayName: form.displayName.trim(),
        role: "tenant_admin",
        tenantId: org.id,
      });
      onCreated(result);
    } catch (err) {
      const msg = err?.message || "Failed to create tenant admin.";
      if (msg.includes("already-exists") || msg.includes("email-already-exists")) {
        setError("A user with this email already exists.");
      } else {
        setError(msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !isSubmitting && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#ffffff",
          color: TC.primary,
          border: "1px solid #d8e6f7",
          borderRadius: "16px",
        },
      }}
    >
      <DialogTitle sx={{ color: TC.primary }}>
        Assign Tenant Admin — {org?.name}
      </DialogTitle>
      <DialogContent>
        {error ? (
          <VuiTypography color="error" variant="button" mb={2} display="block">{error}</VuiTypography>
        ) : null}
        <VuiTypography variant="caption" sx={{ color: TC.secondary }} display="block" mb={2}>
          Create a new user as the tenant admin for this organisation. They will receive a password reset link by email.
        </VuiTypography>
        <VuiBox component="form" id="assign-admin-form" onSubmit={handleSubmit}>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <VuiInput placeholder="Email *" value={form.email} onChange={handleChange("email")} required />
            </Grid>
            <Grid item xs={12}>
              <VuiInput placeholder="Display Name *" value={form.displayName} onChange={handleChange("displayName")} required />
            </Grid>
          </Grid>
        </VuiBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <VuiButton color="text" size="small" onClick={onClose} disabled={isSubmitting}>Cancel</VuiButton>
        <VuiButton color="info" size="small" type="submit" form="assign-admin-form" disabled={isSubmitting}>
          {isSubmitting ? "Creating..." : "Create & Invite"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
}

function Organisations(){
  const { isSuperAdmin } = useAuthz();
  const { companies, selectedCompanyId, selectCompany, refreshCompanies } = useCompany();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [assignAdminOrg, setAssignAdminOrg] = useState(null);
  const [adminsByOrg, setAdminsByOrg] = useState({}); // { orgId: [user, ...] }
  const [backupLoading, setBackupLoading] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(null);
  const restoreInputRef = useRef(null);
  const restoreOrgRef = useRef(null);

  const loadAdminsForOrgs = useCallback(async () => {
    if (!companies.length) return;
    const result = {};
    await Promise.all(
      companies.map(async (org) => {
        try {
          const data = await listTenantUsers({ tenantId: org.id, limit: 200 });
          const users = Array.isArray(data.users) ? data.users : [];
          result[org.id] = users.filter((u) => u.role === "tenant_admin");
        } catch {
          result[org.id] = [];
        }
      })
    );
    setAdminsByOrg(result);
  }, [companies]);

  useEffect(() => { loadAdminsForOrgs(); }, [loadAdminsForOrgs]);

  const handleRemoveAdmin = async (org, admin) => {
    try {
      await deleteTenantUser({ uid: admin.uid || admin.id, tenantId: org.id });
      setSuccessMsg(`Removed "${admin.displayName || admin.email}" from "${org.name}".`);
      loadAdminsForOrgs();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (err) {
      setSuccessMsg(`Error: ${err?.message || "Failed to remove admin."}`);
      setTimeout(() => setSuccessMsg(""), 4000);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.id || "").toLowerCase().includes(q) ||
        (c.legalForm || "").toLowerCase().includes(q)
    );
  }, [companies, search]);

  if (!isSuperAdmin) return <Redirect to="/unauthorized" />;

  const handleCreated = (org) => {
    setCreateOpen(false);
    setSuccessMsg(`Organisation "${org.name}" created successfully.`);
    refreshCompanies();
    setTimeout(() => setSuccessMsg(""), 4000);
  };

  const handleUpdated = (org) => {
    setEditOrg(null);
    setSuccessMsg(`Organisation "${org.name}" updated successfully.`);
    refreshCompanies();
    setTimeout(() => setSuccessMsg(""), 4000);
  };

  const handleAdminCreated = (user) => {
    setAssignAdminOrg(null);
    setSuccessMsg(`Tenant admin "${user.email}" created for "${assignAdminOrg?.name}".`);
    loadAdminsForOrgs();
    setTimeout(() => setSuccessMsg(""), 4000);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    setIsDeleting(true);
    try {
      await deleteOrg(confirmDelete.id);
      setSuccessMsg(`Organisation "${confirmDelete.name}" deleted.`);
      setConfirmDelete(null);
      refreshCompanies();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (err) {
      setSuccessMsg(`Error: ${err?.message || "Failed to delete."}`);
      setConfirmDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBackup = async (org) => {
    setBackupLoading(org.id);
    try {
      const data = await backupTenant(org.id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(org.name || org.id).replace(/\s+/g, "-")}-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setSuccessMsg(`Backup of "${org.name}" downloaded.`);
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (err) {
      setSuccessMsg(`Error: ${err?.message || "Backup failed."}`);
      setTimeout(() => setSuccessMsg(""), 4000);
    } finally {
      setBackupLoading(null);
    }
  };

  const openRestorePicker = (org) => {
    restoreOrgRef.current = org;
    if (restoreInputRef.current) {
      restoreInputRef.current.value = "";
      restoreInputRef.current.click();
    }
  };

  const handleRestoreFileChange = async (event) => {
    const file = event.target.files?.[0];
    const org = restoreOrgRef.current;

    if (!file || !org) return;

    setRestoreLoading(org.id);
    try {
      const parsedData = JSON.parse(await file.text());
      await restoreTenant(org.id, parsedData);
      setSuccessMsg(`Restore for "${org.name}" completed successfully.`);
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (err) {
      setSuccessMsg(`Error: ${err?.message || "Restore failed."}`);
      setTimeout(() => setSuccessMsg(""), 4000);
    } finally {
      event.target.value = "";
      restoreOrgRef.current = null;
      setRestoreLoading(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Header */}
        <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <VuiTypography variant="lg" sx={{ color: TC.primary }}>
            Organisations
          </VuiTypography>
          <VuiButton color="info" size="small" onClick={() => setCreateOpen(true)}>
            + New Organisation
          </VuiButton>
        </VuiBox>

        {successMsg ? (
          <VuiTypography color="success" variant="button" mb={2} display="block">{successMsg}</VuiTypography>
        ) : null}

        {/* Org list */}
        <Card>
          <VuiBox p={3}>
            <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={3} gap={2} flexWrap="wrap">
              <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary }}>
                All Organisations {companies.length > 0 ? `(${companies.length})` : ""}
              </VuiTypography>
              <VuiBox sx={{ minWidth: 240, flex: 1, maxWidth: 360 }}>
                <VuiInput
                  placeholder="Search by name, ID or legal form..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  inputProps={{ "aria-label": "Search organisations" }}
                  icon={{ component: <SearchIcon aria-hidden="true" />, direction: "left" }}
                />
              </VuiBox>
            </VuiBox>

            {companies.length === 0 ? (
              <VuiTypography variant="button" sx={{ color: TC.primary }}>
                No organisations yet. Create one to get started.
              </VuiTypography>
            ) : filtered.length === 0 ? (
              <VuiTypography variant="button" sx={{ color: TC.primary }}>No organisations match your search.</VuiTypography>
            ) : (
              <Grid container spacing={2}>
                {filtered.map((org) => {
                  const isSelected = org.id === selectedCompanyId;
                  return (
                    <Grid item xs={12} md={6} key={org.id}>
                      <VuiBox
                        p={2}
                        borderRadius="lg"
                        sx={{
                          border: isSelected
                            ? "1px solid rgba(0,117,255,0.6)"
                            : "1px solid rgba(255,255,255,0.08)",
                          background: isSelected
                            ? "rgba(0,117,255,0.08)"
                            : "rgba(255,255,255,0.02)",
                          transition: "all 0.2s",
                        }}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={1}
                      >
                        <VuiBox display="flex" gap={1.5} alignItems="flex-start">
                          <BusinessIcon sx={{ color: isSelected ? "#0075ff" : "rgba(255,255,255,0.4)", mt: 0.3 }} />
                          <VuiBox>
                            <VuiBox display="flex" alignItems="center" gap={1}>
                              <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary }}>
                                {org.name}
                              </VuiTypography>
                              {isSelected && (
                                <VuiBadge color="info" size="xs" container>Active</VuiBadge>
                              )}
                            </VuiBox>
                            {org.address && (
                              <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>
                                {org.address}
                              </VuiTypography>
                            )}
                            {org.legalForm && (
                              <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>
                                {org.legalForm}{org.utr ? ` · UTR: ${org.utr}` : ""}
                              </VuiTypography>
                            )}
                            {/* Tenant Admins */}
                            {(adminsByOrg[org.id] || []).length > 0 ? (
                              <VuiBox mt={1}>
                                <VuiTypography variant="caption" fontWeight="bold" display="block" sx={{ color: TC.primary, mb: 0.5 }}>
                                  Tenant Admins:
                                </VuiTypography>
                                {adminsByOrg[org.id].map((admin) => (
                                  <VuiBox
                                    key={admin.uid || admin.id}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={0.5}
                                  >
                                    <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                                      {admin.displayName || admin.email} ({admin.email})
                                    </VuiTypography>
                                    <VuiButton
                                      color="error"
                                      size="small"
                                      variant="text"
                                      sx={{ minWidth: "auto", px: 1, py: 0, fontSize: "11px" }}
                                      onClick={() => handleRemoveAdmin(org, admin)}
                                    >
                                      Remove
                                    </VuiButton>
                                  </VuiBox>
                                ))}
                              </VuiBox>
                            ) : (
                              <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary, mt: 0.5, fontStyle: "italic" }}>
                                No tenant admins assigned
                              </VuiTypography>
                            )}
                          </VuiBox>
                        </VuiBox>

                        <VuiBox display="flex" flexDirection="column" gap={1} alignItems="flex-end">
                          {!isSelected && (
                            <VuiButton
                              color="info"
                              size="small"
                              variant="outlined"
                              onClick={() => selectCompany(org.id)}
                            >
                              Select
                            </VuiButton>
                          )}
                          <VuiButton
                            color="warning"
                            size="small"
                            variant="outlined"
                            onClick={() => setEditOrg(org)}
                          >
                            Edit
                          </VuiButton>
                          <VuiButton
                            color="info"
                            size="small"
                            variant="outlined"
                            onClick={() => setAssignAdminOrg(org)}
                          >
                            Assign Admin
                          </VuiButton>
                          <VuiButton
                            color="info"
                            size="small"
                            variant="outlined"
                            disabled={backupLoading === org.id}
                            onClick={() => handleBackup(org)}
                          >
                            {backupLoading === org.id ? "Backing up..." : "Backup"}
                          </VuiButton>
                          <VuiButton
                            color="warning"
                            size="small"
                            variant="outlined"
                            disabled={restoreLoading === org.id}
                            onClick={() => openRestorePicker(org)}
                          >
                            {restoreLoading === org.id ? "Restoring..." : "Restore"}
                          </VuiButton>
                          <VuiButton
                            color="error"
                            size="small"
                            onClick={() => setConfirmDelete(org)}
                          >
                            Delete
                          </VuiButton>
                        </VuiBox>
                      </VuiBox>
                    </Grid>
                  );
                })}
              </Grid>
            )}
            <input
              ref={restoreInputRef}
              type="file"
              accept=".json"
              style={{ display: "none" }}
              onChange={handleRestoreFileChange}
            />
          </VuiBox>
        </Card>
      </VuiBox>

      <OrgCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} />
      <OrgEditDialog open={Boolean(editOrg)} org={editOrg} onClose={() => setEditOrg(null)} onUpdated={handleUpdated} />
      <AssignTenantAdminDialog open={Boolean(assignAdminOrg)} org={assignAdminOrg} onClose={() => setAssignAdminOrg(null)} onCreated={handleAdminCreated} />

      {/* Delete confirmation dialog */}
      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => !isDeleting && setConfirmDelete(null)}
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            color: TC.primary,
            border: "1px solid #d8e6f7",
            borderRadius: "16px",
            minWidth: 340,
          },
        }}
      >
        <DialogTitle sx={{ color: TC.primary, pb: 1 }}>Delete Organisation</DialogTitle>
        <DialogContent>
          <VuiTypography variant="button" sx={{ color: TC.primary }}>
            Are you sure you want to permanently delete{" "}
            <strong style={{ color: TC.primary }}>{confirmDelete?.name}</strong>?
            This will remove the organisation record. User accounts are not deleted automatically.
          </VuiTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <VuiButton color="text" size="small" onClick={() => setConfirmDelete(null)} disabled={isDeleting}>
            Cancel
          </VuiButton>
          <VuiButton color="error" size="small" onClick={handleDeleteConfirm} disabled={isDeleting}>
            {isDeleting ? "Deleting..." : "Delete"}
          </VuiButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Organisations;

